import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Checkbox,
    FormControlLabel,
    Typography,
    Box,
    IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

interface DeclarationModalProps {
    open: boolean;
    onClose: () => void;
    onProceed: () => void;
    title?: string;
}

const DeclarationModal: React.FC<DeclarationModalProps> = ({
    open,
    onClose,
    onProceed,
    title = "New Application",
}) => {
    const [isChecked, setIsChecked] = useState(false);

    const handleClose = () => {
        setIsChecked(false);
        onClose();
    };

    const handleProceed = () => {
        if (isChecked) {
            onProceed();
            setIsChecked(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "8px",
                },
            }}>
            <DialogTitle
                sx={{
                    fontSize: "1.35rem",
                    fontWeight: "bold",
                    backgroundColor: "#F4F2F7",
                    borderBottom: "1px solid #E7E8E9",
                    lineHeight: "normal",
                }}            >
                Important
            </DialogTitle>

            <DialogContent
                sx={{
                    pt: 3,
                    pb: 2,
                    mt: 2,
                }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontSize: "14px", fontWeight: "bold", color: "#3a1c73", mb: 1 }}>
                        Please read the following instructions carefully:
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "flex-start", mb: 0.5 }}>
                        <Typography
                            component="span"
                            sx={{
                                color: "#3a1c73",
                                fontSize: "14px",
                                fontWeight: "bold",
                                mr: 1,
                                mt: "-1px",
                            }}
                        >
                            •
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: "12.5px",
                                fontWeight: 400,
                                lineHeight: 1.6,
                                color: "#374151",
                            }}
                        >
                            Applications on the Rajmarg Pravesh Portal shall be prepared, submitted,
                            and managed only by the Applicant or an Authorized Representative of the
                            Applicant Organization.
                        </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "flex-start", mb: 0.5 }}>
                        <Typography
                            component="span"
                            sx={{
                                color: "#3a1c73",
                                fontSize: "14px",
                                fontWeight: "bold",
                                mr: 1,
                                mt: "-1px",
                            }}
                        >
                            •
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: "12.5px",
                                fontWeight: 400,
                                lineHeight: 1.6,
                                color: "#374151",
                            }}
                        >
                            Submission of applications through unauthorized consultants, agents,
                            intermediaries, or any other third party is strictly prohibited.
                        </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "flex-start", mb: 0.5 }}>
                        <Typography
                            component="span"
                            sx={{
                                color: "#3a1c73",
                                fontSize: "14px",
                                fontWeight: "bold",
                                mr: 1,
                                mt: "-1px",
                            }}
                        >
                            •
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: "12.5px",
                                fontWeight: 400,
                                lineHeight: 1.6,
                                color: "#374151",
                            }}
                        >
                            The Ministry of Road Transport & Highways (MoRTH) shall not be responsible
                            for any incorrect information, unauthorized submission, or disputes
                            arising from such submissions.
                        </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                        <Typography
                            component="span"
                            sx={{
                                color: "#3a1c73",
                                fontSize: "14px",
                                fontWeight: "bold",
                                mr: 1,
                                mt: "-1px",
                            }}
                        >
                            •
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: "12.5px",
                                fontWeight: 400,
                                lineHeight: 1.6,
                                color: "#374151",
                            }}
                        >
                            By proceeding, you confirm that you are the Applicant or an Authorized
                            Representative and that all information submitted will be true, accurate,
                            and complete.
                        </Typography>
                    </Box>
                </Box>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={isChecked}
                            onChange={(e) => setIsChecked(e.target.checked)}
                            sx={{
                                color: "#9CA3AF",
                                "&.Mui-checked": {
                                    color: "#3a1c73",
                                },
                            }}
                        />
                    }
                    label={
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "14px" }} className="gray-800">
                            I have read and understood the above declaration and confirm that I
                            am the Applicant or an Authorized Representative of the Applicant
                            Organization.
                        </Typography>
                    }
                    sx={{
                        alignItems: "flex-start",
                        marginLeft: 0,
                    }}
                    componentsProps={{
                        typography: {
                            sx: {
                                marginTop: "-2px", // Fine-tune alignment
                            },
                        },
                    }}
                />
            </DialogContent>

            <DialogActions
                sx={{
                    borderTop: "1px solid #E7E8E9",
                    p: 2,
                    paddingX: "24px",
                    paddingY: "16px",
                    gap: 1,
                }}
            >
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    sx={{
                        borderColor: "#3a1c73",
                        color: "#3a1c73",
                        borderRadius: "2rem",
                        textTransform: "none",
                        paddingX: "24px",
                        "&:hover": {
                            borderColor: "#2d1658",
                            backgroundColor: "rgba(58, 28, 115, 0.04)",
                        },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleProceed}
                    variant="contained"
                    disabled={!isChecked}
                    sx={{
                        backgroundColor: "#3a1c73",
                        color: "white",
                        borderRadius: "2rem",
                        textTransform: "none",
                        paddingX: "24px",
                        "&:hover": {
                            backgroundColor: "#2d1658",
                        },
                        "&.Mui-disabled": {
                            backgroundColor: "#9E9E9E",
                            color: "#fff",
                        },
                    }}
                >
                    Proceed
                </Button>
            </DialogActions>
        </Dialog >
    );
};

export default DeclarationModal;